<template>
  <div id="dialog-giftshowDetail">
    <div class="dialog">
      <span class="cls" @click="oth_viewDetail(false)"></span>
      <div class="paper">
        <!-- 헤더 -->
        <div class="header">
          <h2>상품 상세</h2>
          <button class="close-btn" @click="oth_viewDetail(false)">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>

        <!-- 로딩 -->
        <div v-if="loading" class="loading">
          <i class="ph-bold ph-spinner"></i>
          <span>불러오는 중...</span>
        </div>

        <!-- 상품 정보 -->
        <div v-else-if="goodsDetail" class="content">
          <!-- 상품 이미지 -->
          <div class="goods-image">
            <img :src="goodsDetail.goodsImgB" :alt="goodsDetail.goodsName" />
          </div>

          <!-- 브랜드 & 카테고리 -->
          <div class="brand-info">
            <img 
              v-if="goodsDetail.brandIconImg" 
              :src="goodsDetail.brandIconImg" 
              :alt="goodsDetail.brandName"
              class="brand-icon"
            />
            <div class="brand-text">
              <span class="brand-name">{{ goodsDetail.brandName }}</span>
              <span class="category">{{ goodsDetail.categoryName1 }}</span>
            </div>
          </div>

          <!-- 상품명 -->
          <h3 class="goods-name">{{ goodsDetail.goodsName }}</h3>

          <!-- 가격 정보 -->
          <div class="price-info">
            <div class="price-row">
              <span class="label">정가</span>
              <span class="original-price">{{ formatPrice(goodsDetail.realPrice) }}원</span>
            </div>
            <div class="price-row main">
              <span class="label">판매가</span>
              <div class="sale-price-wrap">
                <span class="discount-rate">{{ goodsDetail.discountRate }}%</span>
                <span class="sale-price">{{ formatPrice(goodsDetail.discountPrice) }}원</span>
              </div>
            </div>
          </div>

          <!-- 수량 선택 -->
          <div class="quantity-section">
            <span class="quantity-label">구매 수량</span>
            <div class="quantity-control">
              <button @click="decreaseQty" :disabled="quantity <= 1">
                <i class="ph-bold ph-minus"></i>
              </button>
              <span class="quantity-value">{{ quantity }}</span>
              <button @click="increaseQty" :disabled="quantity >= maxQuantity">
                <i class="ph-bold ph-plus"></i>
              </button>
            </div>
          </div>

          <!-- 결제 요약 -->
          <div class="payment-summary">
            <div class="summary-row">
              <span>상품 금액</span>
              <span>{{ formatPrice(goodsDetail.discountPrice) }} × {{ quantity }}개</span>
            </div>
            <div class="summary-row total">
              <span>총 결제 포인트</span>
              <strong>{{ formatPrice(totalPrice) }}P</strong>
            </div>
            <div class="summary-row balance">
              <span>내 포인트</span>
              <strong :class="{ insufficient: myPoint < totalPrice }">
                {{ formatPrice(myPoint) }}P
              </strong>
            </div>
            <div class="summary-row after" v-if="myPoint >= totalPrice">
              <span>결제 후 잔액</span>
              <span>{{ formatPrice(myPoint - totalPrice) }}P</span>
            </div>
          </div>

          <!-- 상품 정보 요약 -->
          <div class="info-grid">
            <div class="info-item">
              <i class="ph-bold ph-calendar"></i>
              <div>
                <span class="info-label">유효기간</span>
                <span class="info-value">발송일로부터 {{ goodsDetail.limitDay }}일</span>
              </div>
            </div>
            <div class="info-item">
              <i class="ph-bold ph-storefront"></i>
              <div>
                <span class="info-label">사용처</span>
                <span class="info-value">{{ goodsDetail.affiliate }}</span>
              </div>
            </div>
          </div>

          <!-- 사용안내 (접기) -->
          <details class="usage-info">
            <summary>사용안내</summary>
            <pre class="usage-content">{{ goodsDetail.content }}</pre>
          </details>
        </div>

        <!-- 에러 -->
        <div v-else class="error">
          <i class="ph-bold ph-warning-circle"></i>
          <span>상품 정보를 불러올 수 없습니다.</span>
        </div>

        <!-- 하단 구매 버튼 -->
        <div class="footer" v-if="goodsDetail && !loading">
          <div class="footer-price">
            <span>{{ quantity }}개 · 총 결제</span>
            <strong>{{ formatPrice(totalPrice) }}P</strong>
          </div>
          <button 
            class="purchase-btn" 
            :disabled="!canPurchase || purchasing"
            @click="handlePurchase"
          >
            <i class="ph-bold ph-spinner" v-if="purchasing"></i>
            <span v-else>{{ purchaseButtonText }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';

export default {
  name: 'giftshowDetail',
  props: {
    goods_code: {
      type: String,
      required: true
    },
    oth_viewDetail: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const getGiftShowGoodsDetail = inject('getGiftShowGoodsDetail');
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const showToast = inject('showToast', () => {});
    
    const goodsDetail = ref(null);
    const loading = ref(true);
    const purchasing = ref(false);
    const myPoint = ref(0);
    const quantity = ref(1);

    const formatPrice = (price) => {
      return price?.toLocaleString() || '0';
    };

    // 총 가격
    const totalPrice = computed(() => {
      return (goodsDetail.value?.discountPrice || 0) * quantity.value;
    });

    // 최대 구매 가능 수량 (포인트 기준, 최대 10개)
    const maxQuantity = computed(() => {
      if (!goodsDetail.value) return 1;
      return Math.min(10, Math.max(1, Math.floor(myPoint.value / goodsDetail.value.discountPrice)));
    });

    function increaseQty() {
      if (quantity.value < maxQuantity.value) quantity.value++;
    }

    function decreaseQty() {
      if (quantity.value > 1) quantity.value--;
    }

    // 내 포인트 조회
    async function fetchMyPoint() {
      const res = await sql_query(`SELECT point FROM member WHERE mb_id = '${member_info.mb_id}'`);
      myPoint.value = res?.[0]?.point || 0;
    }

    // 구매 가능 여부
    const canPurchase = computed(() => {
      if (!goodsDetail.value) return false;
      if (myPoint.value < totalPrice.value) return false;
      return true;
    });

    // 버튼 텍스트
    const purchaseButtonText = computed(() => {
      if (myPoint.value < totalPrice.value) return '포인트 부족';
      return `${quantity.value}개 구매하기`;
    });

    // 구매 처리
    async function handlePurchase() {
      if (!canPurchase.value || purchasing.value) return;

      const confirmed = confirm(
        `${goodsDetail.value.goodsName}\n` +
        `수량: ${quantity.value}개\n` +
        `총 ${formatPrice(totalPrice.value)}P를 사용하여 구매하시겠습니까?`
      );

      if (!confirmed) return;

      purchasing.value = true;

      try {
        // 유효기간 계산
        const expire_date = new Date();
        expire_date.setDate(expire_date.getDate() + (goodsDetail.value.limitDay || 30));
        const ktgift_expire_date = expire_date.toISOString().slice(0, 19).replace('T', ' ');

        // 메모 데이터 (상품 정보만, 받는 사람 정보 없음)
        const memo_data = {
          goods_name: goodsDetail.value.goodsName,
          goods_img: goodsDetail.value.goodsImgS,
          brand_name: goodsDetail.value.brandName,
          brand_img: goodsDetail.value.brandIconImg || '',
          sale_price: goodsDetail.value.discountPrice
        };
        const memo_str = JSON.stringify(memo_data).replace(/'/g, "\\'");

        // 수량만큼 쿠폰 INSERT (한번에)
        const values = [];
        for (let i = 0; i < quantity.value; i++) {
          const coupon_code = `GC${Date.now()}${i}${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
          values.push(`(
            '${coupon_code}',
            '${goodsDetail.value.brandName}',
            ${goodsDetail.value.discountPrice},
            '${member_info.mb_id}',
            '발행',
            '${props.goods_code}',
            '${ktgift_expire_date}',
            '${memo_str}'
          )`);
        }

        await sql_query(`
          INSERT INTO giftcoupon (
            coupon_code, coupon_type, amount,
            issued_by, status, ktgift_product_code,
            ktgift_expire_date, memo
          ) VALUES ${values.join(',\n')}
        `);

        // 포인트 차감
        await sql_query(`
          UPDATE member 
          SET point = point - ${totalPrice.value}
          WHERE mb_id = '${member_info.mb_id}'
        `);

        // 잔액 조회
        const updated = await sql_query(
          `SELECT point FROM member WHERE mb_id = '${member_info.mb_id}'`
        );
        myPoint.value = updated?.[0]?.point || 0;

        showToast(`${goodsDetail.value.goodsName} ${quantity.value}개 구매 완료!`, 'success');
        
        alert(
          `구매 완료!\n\n` +
          `${goodsDetail.value.goodsName} × ${quantity.value}개\n` +
          `${formatPrice(totalPrice.value)}P 결제\n` +
          `잔여 포인트: ${formatPrice(myPoint.value)}P\n\n` +
          `선물 보유리스트에서 확인하세요.`
        );

        props.oth_viewDetail('purchased');

      } catch (e) {
        console.error('구매 오류:', e);
        showToast(e.message || '구매 중 오류 발생', 'error');
      } finally {
        purchasing.value = false;
      }
    }

    onMounted(async () => {
      loading.value = true;
      await Promise.all([
        getGiftShowGoodsDetail(props.goods_code).then(res => {
          goodsDetail.value = res.goodsDetail;
        }),
        fetchMyPoint()
      ]);
      loading.value = false;
    });

    return {
      goodsDetail, loading, purchasing,
      formatPrice, myPoint, quantity,
      totalPrice, maxQuantity,
      increaseQty, decreaseQty,
      canPurchase, purchaseButtonText,
      handlePurchase
    };
  }
}
</script>

<style scoped>
#dialog-giftshowDetail {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
}

#dialog-giftshowDetail .dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(4px);
}

#dialog-giftshowDetail .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.paper {
  width: 90%;
  max-width: 480px;
  max-height: 85vh;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.close-btn {
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  border-radius: var(--radius-full);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-surface);
  color: var(--color-text-primary);
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px;
  color: var(--color-text-secondary);
}

.loading i {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.goods-image {
  width: 100%;
  max-height: 200px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.brand-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  object-fit: contain;
  border: 1px solid var(--color-border);
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.category {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.goods-name {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  line-height: 1.3;
}

.price-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-row .label {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
}

.original-price {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  text-decoration: line-through;
}

.price-row.main {
  padding-top: 6px;
  border-top: 1px dashed var(--color-border);
}

.price-row.main .label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.sale-price-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.discount-rate {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-danger);
}

.sale-price {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

/* 수량 선택 */
.quantity-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
}

.quantity-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.quantity-control {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.quantity-control button {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-background);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.15s;
}

.quantity-control button:hover:not(:disabled) {
  background: var(--color-surface-deep);
}

.quantity-control button:disabled {
  color: var(--color-text-quaternary);
  cursor: not-allowed;
}

.quantity-value {
  width: 44px;
  text-align: center;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  border-left: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  line-height: 36px;
}

/* 결제 요약 */
.payment-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.summary-row.total {
  padding-top: 8px;
  border-top: 1px solid var(--color-border);
}

.summary-row.total strong {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.summary-row.balance strong {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.summary-row.balance strong.insufficient {
  color: var(--color-danger);
}

.summary-row.after {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

/* 정보 그리드 */
.info-grid {
  display: flex;
  gap: 8px;
}

.info-item {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
}

.info-item > i {
  font-size: 16px;
  color: var(--color-primary);
}

.info-item > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.info-value {
  font-size: var(--font-size-xs);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.usage-info {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.usage-info summary {
  padding: 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  background: var(--color-surface);
}

.usage-content {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: keep-all;
  font-family: inherit;
  padding: 12px;
  max-height: 150px;
  overflow-y: auto;
}

.footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  flex-shrink: 0;
}

.footer-price {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.footer-price span {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.footer-price strong {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.purchase-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
}

.purchase-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.purchase-btn:disabled {
  background: var(--color-text-quaternary);
  cursor: not-allowed;
}

.purchase-btn i {
  animation: spin 1s linear infinite;
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: var(--color-text-tertiary);
}

.error i {
  font-size: 40px;
  color: var(--color-danger);
}

@media (max-width: 480px) {
  .paper {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .goods-image {
    max-height: 160px;
  }

  .info-grid {
    flex-direction: column;
  }
}
</style>